/**
 * PQ-004 — a catalogue of user-facing messages, keyed by the stable error codes the API
 * publishes. Each entry says what happened and what the person can do about it; none of
 * them exposes an internal code, a stack trace or database jargon.
 *
 * Copy is neutral professional Spanish: no regional voseo, no colloquialisms.
 */

export interface UserFacingError {
  message: string;
  /** Suggested next step, shown alongside the message when there is a useful one. */
  hint?: string;
}

const CATALOG: Readonly<Record<string, UserFacingError>> = {
  INVALID_CREDENTIALS: {
    message: 'El correo o la contraseña no son correctos.',
    hint: 'Verifique los datos e intente nuevamente.',
  },
  SESSION_EXPIRED: {
    message: 'Su sesión ha expirado.',
    hint: 'Inicie sesión nuevamente para continuar.',
  },
  FORBIDDEN: {
    message: 'No cuenta con permisos para realizar esta operación.',
    hint: 'Si considera que debería tener acceso, comuníquese con Recursos Humanos.',
  },
  ORIGIN_NOT_ALLOWED: {
    message: 'La solicitud fue rechazada por motivos de seguridad.',
    hint: 'Vuelva a cargar la página e intente nuevamente.',
  },
  VALIDATION_FAILED: {
    message: 'Algunos datos del formulario no son válidos.',
    hint: 'Revise los campos marcados y vuelva a enviar.',
  },
  EMPLOYEE_NOT_FOUND: {
    message: 'No se encontró la persona solicitada.',
    hint: 'Es posible que el registro haya sido dado de baja.',
  },
  DEPARTMENT_NOT_FOUND: {
    message: 'El departamento seleccionado ya no existe.',
    hint: 'Actualice la página para ver la lista vigente.',
  },
  LOCATION_NOT_FOUND: {
    message: 'La ubicación seleccionada ya no existe.',
    hint: 'Actualice la página para ver la lista vigente.',
  },
  EMPLOYEE_EMAIL_TAKEN: {
    message: 'Ya existe otra persona registrada con ese correo.',
    hint: 'Utilice un correo distinto o busque el registro existente.',
  },
  EMPLOYEE_NUMBER_TAKEN: {
    message: 'Ya existe otra persona con ese número de legajo.',
    hint: 'Utilice un número distinto.',
  },
  EMPLOYEE_DUPLICATE: {
    message: 'Ya existe una persona con ese correo o número de legajo.',
    hint: 'Busque el registro existente antes de crear uno nuevo.',
  },
  EMPLOYEE_SELF_MANAGER: {
    message: 'Una persona no puede ser responsable de sí misma.',
    hint: 'Seleccione otro responsable.',
  },
  EMPLOYEE_REPORTING_CYCLE: {
    message: 'El responsable seleccionado depende de esta persona.',
    hint: 'Asignarlo generaría un ciclo en la línea de reporte. Seleccione otro responsable.',
  },
  EMPLOYEE_TERMINATED: {
    message: 'Esta persona fue dada de baja y no puede modificarse.',
  },
  EMPLOYEE_ALREADY_TERMINATED: {
    message: 'Esta persona ya se encontraba dada de baja.',
  },
  ROUTE_NOT_FOUND: {
    message: 'No se encontró el recurso solicitado.',
  },
  INTERNAL_ERROR: {
    message: 'No pudimos completar la operación.',
    hint: 'Intente nuevamente en unos instantes. Si persiste, comparta el código de referencia con soporte.',
  },
  NETWORK_ERROR: {
    message: 'No pudimos comunicarnos con el servidor.',
    hint: 'Verifique su conexión e intente nuevamente.',
  },
  TIMEOUT: {
    message: 'La operación tardó más de lo esperado.',
    hint: 'Intente nuevamente.',
  },
  RATE_LIMITED: {
    message: 'Se registraron demasiados intentos.',
    hint: 'Espere unos minutos antes de volver a intentar.',
  },
};

const FALLBACK: UserFacingError = {
  message: 'Ocurrió un inconveniente al procesar la solicitud.',
  hint: 'Intente nuevamente. Si el problema persiste, comparta el código de referencia con soporte.',
};

export function describeError(code: string, status?: number): UserFacingError {
  if (status === 429) {
    return CATALOG.RATE_LIMITED ?? FALLBACK;
  }
  return CATALOG[code] ?? FALLBACK;
}
